import React, { useEffect, useState } from 'react';
import _merge from 'lodash/merge';
import _debounce from 'lodash/debounce';

import { getContext } from 'common';
import { getElevationStyle, ElevationProps } from 'components/Elevation';
import Styled from './styles';

const BREAKPOINT_4 = 767;
const BREAKPOINT_7 = 768;
const BREAKPOINT_7_END = 991;
const BREAKPOINT_9 = 1119;
const BREAKPOINT_12 = 1200;

const DEVICE_TYPES = { mobile: 'Mobile', tablet: 'Tablet', default: 'Large' };
const BREAKPOINTS = {
  mobile: `(max-width: ${BREAKPOINT_4}px)`,
  tablet: `screen and (min-width: ${BREAKPOINT_7}px) and (max-width: ${BREAKPOINT_9}px)`,
  // tabs have a 'special' breakpoint value to help them fit on one row of the AppToolbar in smaller displays
  breakpoint7: `(max-width: ${BREAKPOINT_7_END}px)`,
  default: `(min-width: ${BREAKPOINT_12}px)`,
};

const AppToolbarContext = React.createContext<any>({});

interface AppToolbarProps extends React.ComponentPropsWithRef<'div'> {
  /** Place AppToolbar content here, eg. AppToolbarTitleContainer, AppToolbarTitle, AppToolbarSubtitle, AppToolbarContent */
  children?: string | React.ReactNode;
  /** Returns the current device type, 'Mobile', 'Tablet', 'Large'. */
  getDeviceType?: (arg: string) => void;
  /** Returns true if the display width is small enough to require a smaller min-width of a Tab. */
  getTabBreakPoint?: (arg: boolean) => void;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Override the `Elevation` properties. See `Elevation` component for details. */
  elevationProps?: Partial<ElevationProps>;
  /** To customize the wrapper style. */
  style?: React.CSSProperties;
}

/** Application Toolbar. Any other props will be provided to the root element. */
const AppToolbar = React.forwardRef<HTMLElement, AppToolbarProps>((props: AppToolbarProps, ref) =>
  getContext(({ RTL, theme }) => {
    const {
      children,
      getDeviceType,
      getTabBreakPoint,
      theme: propTheme,
      style: userStyle,
      elevationProps,
      ...otherProps
    } = props;
    const [isMounted, setIsMounted] = useState(false);
    const [matches, setMatches] = useState(DEVICE_TYPES.default);
    const [tabBreakPoint, setTabBreakPoint] = useState(false);
    const resolvedTheme = _merge({}, theme.TOOLBAR, propTheme);
    const resolvedWithElevationStyle = _merge({}, {
      ...getElevationStyle(elevationProps)
    }, userStyle);

    useEffect(() => {
      setIsMounted(true);
      return () => {
        setIsMounted(false);
      };
    }, []);

    useEffect(() => {
      isMounted && getDeviceType && getDeviceType(matches);
    }, [matches]);
    useEffect(() => {
      isMounted && getTabBreakPoint && getTabBreakPoint(tabBreakPoint);
    }, [tabBreakPoint]);

    const mQuery = _debounce(() => {
      // special Breakpoint for Tabs to change the min-width
      const mediaQueryTabs = window.matchMedia(BREAKPOINTS.breakpoint7);
      if (mediaQueryTabs.matches) {
        setTabBreakPoint(true);
      } else {
        setTabBreakPoint(false);
      }

      const mediaQueryMobile = window.matchMedia(BREAKPOINTS.mobile);
      if (mediaQueryMobile.matches) {
        setMatches(DEVICE_TYPES.mobile);
        return;
      }

      const mediaQueryTablet = window.matchMedia(BREAKPOINTS.tablet);
      if (mediaQueryTablet.matches) {
        setMatches(DEVICE_TYPES.tablet);
        return;
      }
      setMatches(DEVICE_TYPES.default);
    }, 100);

    useEffect(() => {
      mQuery();
      window.addEventListener('resize', mQuery);
      return () => {
        document.removeEventListener('resize', mQuery);
      };
    }, []);

    return (
      <Styled.AppToolbar
        ref={ref}
        $rtl={RTL}
        $theme={resolvedTheme}
        style={resolvedWithElevationStyle}
        $deviceType={matches}
        $tabBreakPoint={tabBreakPoint}
        {...otherProps}
      >
        <AppToolbarContext.Provider value={{ deviceType: matches }} >
          {children}
        </AppToolbarContext.Provider>
      </Styled.AppToolbar>
    );
  }));

AppToolbar.displayName = 'AppToolbar';

AppToolbar.defaultProps = {
  children: undefined,
  getDeviceType: undefined,
  getTabBreakPoint: undefined,
  theme: {},
  elevationProps: {
    elevationIndex: 4,
    dark: true,
  },
  style: {}
};

export {
  AppToolbar as default,
  AppToolbarContext,
  AppToolbarProps
};
